import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Scale, Compass, Sparkles, HardHat, Banknote, Users, Building2, ArrowRight, Search, MapPin, Star, ShieldCheck, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { api, fileUrl } from "../lib/api";

const SERVICES = [
    {
        to: "/brokers",
        icon: Users,
        title: "Verified Brokers",
        desc: "RERA-checked property agents to help you buy, sell and rent across cities.",
        color: "from-[#002657] to-[#1a3a6e]",
        accent: "text-gold-400",
        cta: "Find a broker",
    },
    {
        to: "/advocates",
        icon: Scale,
        title: "Property Advocates",
        desc: "Bar Council registered advocates for title checks, drafting and registry.",
        color: "from-[#001a3d] to-[#003366]",
        accent: "text-gold-400",
        cta: "Get legal help",
    },
    {
        to: "/loan-services",
        icon: Banknote,
        title: "Home Loan Agents",
        desc: "Compare rates from 20+ RBI-approved lenders with pre-approval in 3 days.",
        color: "from-[#0a2540] to-[#1e3a5f]",
        accent: "text-gold-400",
        cta: "Explore loans",
    },
    {
        to: "/builders",
        icon: Building2,
        title: "Verified Builders",
        desc: "RERA-registered developers with escrow-backed payments and 5-year delivery track records.",
        color: "from-[#001a3d] to-[#003366]",
        accent: "text-gold-400",
        cta: "Buy from developer",
    },
    {
        to: "/architects",
        icon: Compass,
        title: "Architects",
        desc: "Council-of-Architecture registered pros for concept to occupancy certificate.",
        color: "from-[#002657] to-[#1a3a6e]",
        accent: "text-gold-400",
        cta: "Hire an architect",
    },
    {
        to: "/interior-designers",
        icon: Sparkles,
        title: "Interior Designers",
        desc: "Modular kitchens, wardrobes, false ceilings and turnkey fit-outs with warranty.",
        color: "from-[#001a3d] to-[#003366]",
        accent: "text-gold-400",
        cta: "Design my home",
    },
    {
        to: "/civil-contractors",
        icon: HardHat,
        title: "Civil Contractors",
        desc: "Full construction, renovations, RCC + brickwork with milestone-linked payments.",
        color: "from-[#0a2540] to-[#1e3a5f]",
        accent: "text-gold-400",
        cta: "Get a quote",
    },
];

export default function Services() {
    const [searchParams] = useSearchParams();
    const urlCat  = searchParams.get("cat")  || "";
    const urlCity = searchParams.get("city") || "";
    const [pros, setPros] = useState(null);
    const [categories, setCategories] = useState({});
    const [query, setQuery] = useState(urlCity);
    const [activeCat, setActiveCat] = useState(urlCat || "all");

    // Keep local state in sync when navigating between /discover results.
    useEffect(() => {
        setActiveCat(urlCat || "all");
        setQuery(urlCity);
    }, [urlCat, urlCity]);

    useEffect(() => {
        api.get("/services/professionals", { params: { limit: 200 } })
            .then((r) => {
                setPros(r.data?.items || []);
                setCategories(r.data?.categories || {});
            })
            .catch(() => setPros([]));
    }, []);

    const filtered = useMemo(() => {
        if (!pros) return null;
        const q = query.trim().toLowerCase();
        return pros.filter((p) => {
            if (activeCat !== "all" && p.role !== activeCat) return false;
            if (!q) return true;
            return (
                (p.name || "").toLowerCase().includes(q) ||
                (p.city || "").toLowerCase().includes(q) ||
                (p.category_label || "").toLowerCase().includes(q)
            );
        });
    }, [pros, query, activeCat]);

    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="Property Services — Brokers, Advocates, Loans, Architects, Interiors, Contractors"
                description="Every property professional you need on one platform. Verified brokers, advocates, loan agents, architects, interior designers and civil contractors across India."
                path="/services"
                keywords="property services India, real estate professionals Nagpur, hire broker advocate architect interior contractor"
            />

            {/* HERO */}
            <section className="text-on-image relative bg-gradient-to-br from-[#001a3d] via-[#002657] to-[#12234b] border-b border-gold-500/10 overflow-hidden">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="label-overline mb-3">One platform. Every property need.</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
                        Every professional your <span className="text-gold-500">property journey</span> needs.
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg mt-5 max-w-2xl">
                        From title checks to interior fit-outs, all our partners are KYC-verified, RERA/Bar-Council registered and rated by real customers.
                    </p>
                </div>
            </section>

            {/* SEARCH + ALL PROFESSIONALS DIRECTORY */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-10 sm:pt-14" data-testid="services-directory">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-gold-600 font-extrabold">Portal directory</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy-500">All registered service professionals</h2>
                        <p className="text-sm text-navy-500/60 mt-1">
                            {pros === null ? "Loading…" : <><strong>{filtered?.length ?? 0}</strong> pros available across every category</>}
                        </p>
                    </div>
                    <div className="relative w-full sm:w-80">
                        <Search className="w-4 h-4 text-navy-500/40 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name, city or category…"
                            data-testid="services-search-input"
                            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-navy-500/15 bg-white text-sm focus:outline-none focus:border-gold-500/60"
                        />
                    </div>
                </div>

                {/* Category filter chips */}
                <div className="flex gap-2 overflow-x-auto pb-3 -mx-5 px-5 sm:mx-0 sm:px-0 no-scrollbar">
                    <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} label="All" />
                    {Object.entries(categories).map(([role, label]) => (
                        <CatChip key={role} active={activeCat === role} onClick={() => setActiveCat(role)} label={label} />
                    ))}
                </div>

                {/* Pros grid */}
                {filtered === null ? (
                    <div className="py-12 text-center text-navy-500/50"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
                ) : filtered.length === 0 ? (
                    <div className="py-12 text-center text-navy-500/60 bg-cream-50 rounded-xl border border-navy-500/10">
                        No professionals matched your search. Try another category or clear the search.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((p) => (
                            <Link
                                key={p.id}
                                to={`/services/professional/${p.id}`}
                                data-testid={`services-pro-${p.id}`}
                                className="bg-white border border-navy-500/10 rounded-xl p-4 hover:shadow-lg hover:border-gold-500/40 transition-all flex gap-3"
                            >
                                <div className="w-16 h-16 rounded-lg bg-cream-50 border border-navy-500/10 flex items-center justify-center shrink-0 overflow-hidden">
                                    {p.portfolio_preview?.[0]?.image_url
                                        ? <img src={fileUrl(p.portfolio_preview[0].image_url)} alt="" className="w-full h-full object-cover" />
                                        : <Sparkles className="w-6 h-6 text-gold-500/40" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <div className="font-heading font-bold text-navy-500 text-sm truncate">{p.name}</div>
                                        {p.verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                                    </div>
                                    <div className="text-[11px] text-gold-700 font-bold uppercase tracking-wider mt-0.5">{p.category_label}</div>
                                    <div className="flex items-center gap-2 mt-1 text-[11px] text-navy-500/60">
                                        {p.city && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {p.city}</span>}
                                        {p.reviews_count > 0 && <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-gold-500 text-gold-500" /> {p.rating?.toFixed(1)} · {p.reviews_count}</span>}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* SERVICES GRID */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                    {SERVICES.map((s) => {
                        const Icon = s.icon;
                        return (
                            <Link
                                key={s.to}
                                to={s.to}
                                data-testid={`services-card-${s.to.slice(1)}`}
                                className={`text-on-image group relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.color} p-7 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300`}
                            >
                                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-gold-500/10 blur-3xl group-hover:bg-gold-500/20 transition-colors" />
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center mb-5">
                                        <Icon className="w-5 h-5 text-gold-400" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold text-white mb-2">{s.title}</h3>
                                    <p className="text-sm text-white/70 leading-relaxed mb-5">{s.desc}</p>
                                    <div className="inline-flex items-center gap-1.5 text-xs font-bold text-gold-400 group-hover:text-gold-300 transition-colors">
                                        {s.cta} <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function CatChip({ active, onClick, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`services-catchip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                active
                    ? "bg-navy-500 text-white border-navy-500"
                    : "bg-white text-navy-500/75 border-navy-500/15 hover:border-gold-500/50 hover:text-navy-500"
            }`}
        >
            {label}
        </button>
    );
}

